import { useState } from 'react';
import { simulate, spikesToDecision, type FlyDecision } from './connectome';

interface DebateProps {
  onFiredChange: (maleFired: string[], femaleFired: string[]) => void;
}

interface DebateResult {
  male: FlyDecision;
  female: FlyDecision;
  controversy: number;
  verdict: string;
}

/**
 * Deterministic verdict derived from both runs' spike statistics.
 *
 * This is a threshold on a distance between four numbers. It is not language,
 * it does not read the question, and it is not a judgement about anything. The
 * same input text always yields the same verdict.
 */
function synthesize(male: FlyDecision, female: FlyDecision): DebateResult {
  const controversy = Math.min(1, Math.abs(male.exploration - female.caution) + Math.abs(male.caution - female.exploration)) / 2;

  let verdict: string;
  if (controversy < 0.15) {
    verdict = 'Male-weight and female-weight runs landed close together (controversy below 15%). The two wiring presets agree on this input — arithmetically, not because anything was understood.';
  } else if (controversy < 0.35) {
    verdict = 'Moderate split between the male-weight and female-weight runs (15–35%). The presets disagree slightly on this input; treat any reading of it as illustrative.';
  } else {
    verdict = 'Wide split between the male-weight and female-weight runs (above 35%). The two presets disagree strongly on this input, which says something about these invented weights — not about your question.';
  }

  return { male, female, controversy, verdict };
}

export function Debate({ onFiredChange }: DebateProps) {
  const [question, setQuestion] = useState('Should I deploy on Friday?');
  const [result, setResult] = useState<DebateResult | null>(null);
  const [running, setRunning] = useState(false);

  const run = () => {
    if (!question.trim() || running) return;
    setRunning(true);

    const maleSim = simulate(question, 'male');
    const femaleSim = simulate(question, 'female');
    const male = spikesToDecision(maleSim.state, 'male');
    const female = spikesToDecision(femaleSim.state, 'female');
    const synth = synthesize(male, female);

    // last frame's fired regions for viz
    const maleFired = maleSim.firedHistory[maleSim.firedHistory.length - 1] ?? [];
    const femaleFired = femaleSim.firedHistory[femaleSim.firedHistory.length - 1] ?? [];
    onFiredChange(maleFired, femaleFired);

    setResult(synth);
    setRunning(false);
  };

  return (
    <div>
      <div className="controls">
        <textarea
          className="debate-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Type any text — it is hashed, not read…"
          rows={2}
        />
      </div>
      <div className="controls" style={{ marginTop: '0.75rem' }}>
        <button className="primary" onClick={run} disabled={running || !question.trim()}>
          {running ? '🪰 running 9-node LIF…' : '🪰 Run the simulation'}
        </button>
      </div>

      {result && (
        <>
          <div className="debate-columns">
            <div className="agent-card male">
              <h3>♂ Run with male edge weights — illustrative 9-region graph</h3>
              <div className="agent-output">
                <div className="thinking">spike-driven label:</div>
                {result.male.stance}
                {'\n\n'}exploration: {(result.male.exploration * 100).toFixed(0)}%  caution: {(result.male.caution * 100).toFixed(0)}%
                {'\n'}action readiness: {(result.male.actionReadiness * 100).toFixed(0)}%
              </div>
            </div>
            <div className="agent-card female">
              <h3>♀ Run with female edge weights — illustrative 9-region graph</h3>
              <div className="agent-output">
                <div className="thinking">spike-driven label:</div>
                {result.female.stance}
                {'\n\n'}exploration: {(result.female.exploration * 100).toFixed(0)}%  caution: {(result.female.caution * 100).toFixed(0)}%
                {'\n'}action readiness: {(result.female.actionReadiness * 100).toFixed(0)}%
              </div>
            </div>
          </div>

          <div className="synthesis">
            <h3>🧠 Verdict <span className="view-switcher"><em>threshold on spike stats — no LLM, no synthesis</em></span></h3>
            <div className="synthesis-text">{result.verdict}</div>
            <div className="controversy-meter">
              <span>controversy</span>
              <div className="controversy-bar">
                <div className="controversy-fill" style={{ width: `${result.controversy * 100}%` }} />
              </div>
              <span>{(result.controversy * 100).toFixed(0)}%</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
